import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface GeolocationPositionState {
    latitude: number | null;
    longitude: number | null;
    altitude: number | null;
    accuracy: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
    timestamp: number | null;
}

export interface UseGeolocationOptions {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
    watch?: boolean;
}

const DEFAULT_OPTIONS: Required<Omit<UseGeolocationOptions, "watch">> = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
};

const EMPTY_POSITION: GeolocationPositionState = {
    latitude: null,
    longitude: null,
    altitude: null,
    accuracy: null,
    altitudeAccuracy: null,
    heading: null,
    speed: null,
    timestamp: null,
};

const parseErrorMessage = (error: GeolocationPositionError): string => {
    switch (error.code) {
        case error.PERMISSION_DENIED:
            return "Bạn đã từ chối quyền truy cập vị trí.";
        case error.POSITION_UNAVAILABLE:
            return "Không thể lấy vị trí hiện tại.";
        case error.TIMEOUT:
            return "Hết thời gian lấy vị trí. Vui lòng thử lại.";
        default:
            return "Không thể lấy vị trí hiện tại.";
    }
};

const useGeolocation = (options?: UseGeolocationOptions) => {
    const [position, setPosition] = useState<GeolocationPositionState>(EMPTY_POSITION);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const watchIdRef = useRef<number | null>(null);

    const geoOptions = useMemo(
        () => ({
            ...DEFAULT_OPTIONS,
            enableHighAccuracy: options?.enableHighAccuracy ?? DEFAULT_OPTIONS.enableHighAccuracy,
            timeout: options?.timeout ?? DEFAULT_OPTIONS.timeout,
            maximumAge: options?.maximumAge ?? DEFAULT_OPTIONS.maximumAge,
        }),
        [options?.enableHighAccuracy, options?.maximumAge, options?.timeout]
    );

    const handleSuccess = useCallback((geoPosition: GeolocationPosition) => {
        const coords = geoPosition.coords;
        setPosition({
            latitude: coords.latitude ?? null,
            longitude: coords.longitude ?? null,
            altitude: coords.altitude ?? null,
            accuracy: coords.accuracy ?? null,
            altitudeAccuracy: coords.altitudeAccuracy ?? null,
            heading: coords.heading ?? null,
            speed: coords.speed ?? null,
            timestamp: geoPosition.timestamp ?? null,
        });
        setLoading(false);
        setError(null);
    }, []);

    const handleError = useCallback((geoError: GeolocationPositionError) => {
        setLoading(false);
        setError(parseErrorMessage(geoError));
    }, []);

    const requestCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setError("Trình duyệt không hỗ trợ định vị.");
            setLoading(false);
            return;
        }

        setLoading(true);
        navigator.geolocation.getCurrentPosition(handleSuccess, handleError, geoOptions);
    }, [geoOptions, handleError, handleSuccess]);

    const stopWatching = useCallback(() => {
        if (watchIdRef.current !== null && navigator.geolocation) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!options?.watch) {
            return undefined;
        }

        if (!navigator.geolocation) {
            setError("Trình duyệt không hỗ trợ định vị.");
            return undefined;
        }

        setLoading(true);
        watchIdRef.current = navigator.geolocation.watchPosition(handleSuccess, handleError, geoOptions);

        return () => {
            stopWatching();
        };
    }, [geoOptions, handleError, handleSuccess, options?.watch, stopWatching]);

    return {
        position,
        loading,
        error,
        requestCurrentLocation,
        stopWatching,
    };
};

export default useGeolocation;
